import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  TextField,
  Paper,
  Grid,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
  FormControl,
  FormLabel,
  Switch,
  FormControlLabel,
} from '@mui/material';

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { configurationService } from '../services/configurationService';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SaveIcon from '@mui/icons-material/Save';
import RestoreIcon from '@mui/icons-material/Restore';

interface ConfigState {
  [key: string]: string | number | boolean;
}

export const ConfigurationPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [configState, setConfigState] = useState<ConfigState>({});
  const [hasChanges, setHasChanges] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const { data: configGroups, isLoading } = useQuery({
    queryKey: ['configuration'],
    queryFn: configurationService.getAll,
  });

  // Initialize state from fetched config
  useEffect(() => {
    if (configGroups) {
      const initialState: ConfigState = {};
      configGroups.forEach((group) => {
        group.settings.forEach((config) => {
          initialState[config.key] = config.value;
        });
      });
      setConfigState(initialState);
    }
  }, [configGroups]);

  const updateMutation = useMutation({
    mutationFn: (updates: Array<{ key: string; value: any }>) =>
      Promise.all(
        updates.map((update) => configurationService.update(update.key, { value: update.value }))
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['configuration'] });
      setSaveSuccess(true);
      setHasChanges(false);
      setTimeout(() => setSaveSuccess(false), 3000);
    },
  });

  const handleChange = (key: string, value: any) => {
    setConfigState((prev) => ({ ...prev, [key]: value }));
    setHasChanges(true);
  };

  const handleSave = () => {
    if (!configGroups) return;

    const updates: Array<{ key: string; value: any }> = [];
    configGroups.forEach((group) => {
      group.settings.forEach((config) => {
        if (configState[config.key] !== config.value) {
          updates.push({
            key: config.key,
            value: configState[config.key],
          });
        }
      });
    });

    if (updates.length > 0) {
      updateMutation.mutate(updates);
    }
  };

  const handleReset = () => {
    if (!configGroups) return;

    const initialState: ConfigState = {};
    configGroups.forEach((group) => {
      group.settings.forEach((config) => {
        initialState[config.key] = config.value;
      });
    });
    setConfigState(initialState);
    setHasChanges(false);
  };

  const renderConfigField = (config: any) => {
    const value = configState[config.key];

    switch (config.valueType) {
      case 'boolean':
        return (
          <FormControlLabel
            control={
              <Switch
                checked={Boolean(value)}
                onChange={(e) => handleChange(config.key, e.target.checked)}
              />
            }
            label={config.key.split('.').pop()?.replace(/_/g, ' ').toUpperCase()}
          />
        );

      case 'integer':
        return (
          <TextField
            label={config.key.split('.').pop()?.replace(/_/g, ' ')}
            type="number"
            value={value || 0}
            onChange={(e) => handleChange(config.key, parseInt(e.target.value) || 0)}
            helperText={config.description}
            fullWidth
          />
        );

      case 'string':
        return (
          <TextField
            label={config.key.split('.').pop()?.replace(/_/g, ' ')}
            value={value || ''}
            onChange={(e) => handleChange(config.key, e.target.value)}
            helperText={config.description}
            fullWidth
          />
        );

      case 'interval':
        return (
          <TextField
            label={config.key.split('.').pop()?.replace(/_/g, ' ')}
            value={value || ''}
            onChange={(e) => handleChange(config.key, e.target.value)}
            helperText={config.description + ' (Format: HH:MM:SS or days)'}
            fullWidth
            placeholder="01:00:00"
          />
        );

      default:
        return (
          <TextField
            label={config.key.split('.').pop()?.replace(/_/g, ' ')}
            value={value?.toString() || ''}
            onChange={(e) => handleChange(config.key, e.target.value)}
            helperText={config.description}
            fullWidth
          />
        );
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography color="text.secondary">Loading configuration...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">System Configuration</Typography>
        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            startIcon={<RestoreIcon />}
            onClick={handleReset}
            disabled={!hasChanges}
          >
            Reset
          </Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={!hasChanges || updateMutation.isPending}
          >
            Save Changes
          </Button>
        </Stack>
      </Box>

      {saveSuccess && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Configuration saved successfully!
        </Alert>
      )}

      {hasChanges && (
        <Alert severity="info" sx={{ mb: 3 }}>
          You have unsaved changes
        </Alert>
      )}

      {configGroups?.map((group) => (
        <Accordion key={group.category} defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">{group.category}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={3}>
              {group.settings.map((config) => (
                <Grid item xs={12} md={6} key={config.key}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <FormControl fullWidth>
                      <FormLabel sx={{ mb: 1 }}>
                        <Typography variant="subtitle2" fontWeight="medium">
                          {config.key}
                        </Typography>
                      </FormLabel>
                      {renderConfigField(config)}
                      {config.description && (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                          Current value:{' '}
                          <Typography
                            component="span"
                            variant="caption"
                            fontFamily="monospace"
                            color="primary"
                          >
                            {config.value?.toString()}
                          </Typography>
                        </Typography>
                      )}
                    </FormControl>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </AccordionDetails>
        </Accordion>
      ))}

      {(!configGroups || configGroups.length === 0) && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">No configuration settings available</Typography>
        </Paper>
      )}
    </Box>
  );
};
